import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { fitCanvas } from '../ui/viewport';

/**
 * BootScene: first scene. Generates temporary placeholder textures (spec
 * section 43 — no real art in the MVP), settles the display DPR so later scenes
 * lay out at the correct scale, then hands off to the main menu.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super(SCENES.BOOT);
  }

  create(): void {
    this.createPlaceholderTextures();
    // Some hosts report devicePixelRatio only after first paint. Wait until it
    // stabilizes (or a timeout) before starting the menu, so text renders at
    // the right size without needing a disruptive scene restart later.
    this.settleDprThenStart(-1, 0, 0);
  }

  private settleDprThenStart(
    previousDpr: number,
    stableCount: number,
    elapsedMs: number
  ): void {
    const dpr = fitCanvas(this.game);
    const nextStable = dpr === previousDpr ? stableCount + 1 : 0;

    if (nextStable >= 2 || elapsedMs >= 600) {
      this.scene.start(SCENES.MAIN_MENU);
      return;
    }

    const step = 80;
    this.time.delayedCall(step, () =>
      this.settleDprThenStart(dpr, nextStable, elapsedMs + step)
    );
  }

  /**
   * Build a few flat-color 1x1 textures at runtime so later scenes have
   * sprites to use without shipping any image assets yet.
   */
  private createPlaceholderTextures(): void {
    const swatches: Record<string, number> = {
      'px-grass': 0x2e7d32,
      'px-road': 0x37474f,
      'px-parking': 0x9e9e9e,
      'px-vehicle': 0x1565c0,
      'px-white': 0xffffff,
    };

    for (const [key, color] of Object.entries(swatches)) {
      if (this.textures.exists(key)) continue;
      const g = this.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(color, 1);
      g.fillRect(0, 0, 1, 1);
      g.generateTexture(key, 1, 1);
      g.destroy();
    }
  }
}
